import { useEffect, useState } from 'react';
import { LogService } from '../../services/log-service';
import { GuidService } from 'ui-components/services/guid-service';
import { useEntitiesFulfillmentPlansFromFulfillmentPlanIds } from '../../hooks/entities/selectors/fulfillment-plans';
import { sortByPropAsc } from '../../services/sort-service';
import { getMissingLinks } from './split-ship-api-client';

const getInitialData = (fulfillmentPlans) => {
  const initialFulfillmentPlans = [];
  const initialTableData = [];

  const sortedFulfillmentPlans = [...fulfillmentPlans].sort(sortByPropAsc('createDateTime'));

  sortedFulfillmentPlans.forEach(({ fulfillmentPlanId, items, orderGridStatus }) => {
    initialFulfillmentPlans.push({
      fulfillmentPlanId,
      orderGridStatus,
    });

    initialTableData.push(
      ...items.map(({ productId, fulfillmentPlanItemId, name, sku, quantity, salesOrderItemId, mergeKey }) => {
        return {
          productId,
          dataRowId: GuidService.newGuid(),
          fulfillmentPlanId,
          status: orderGridStatus,
          fulfillmentPlanItemId,
          name,
          salesOrderItemId,
          sku,
          quantity,
          mergeKey,
        };
      }),
    );
  });

  return { initialFulfillmentPlans, initialTableData };
};

const getPatchedTableData = (initialTableData, patchedLinks = []) => {
  if (patchedLinks.length === 0) {
    return initialTableData;
  }

  return initialTableData.map((rowData) => {
    const patchedLinkForRowData = patchedLinks.find(({ fulfillmentPlanItemId }) => fulfillmentPlanItemId === rowData.fulfillmentPlanItemId);
    if (!patchedLinkForRowData) {
      return rowData;
    }
    return {
      ...rowData,
      salesOrderItemId: patchedLinkForRowData.salesOrderItemId,
    };
  });
};

const handlePatchFailure = (missingLinks) => {
  const fpItemIdsWithMissingLinks = missingLinks.map(({ fulfillmentPlanItemId }) => fulfillmentPlanItemId).join(', ');
  LogService.logError(new Error('Failed to patch missing salesOrderItem link for fulfillmentPlanItems'), { fpItemIdsWithMissingLinks });
};

const filterItemsWithMissingLinks = (items) => items.filter((item) => !item.mergeKey && !item.salesOrderItemId);

const getItemsWithMissingLinks = (fulfillmentPlans) => {
  return fulfillmentPlans.flatMap((fulfillmentPlan) => {
    const itemsWithMissingLinks = filterItemsWithMissingLinks(fulfillmentPlan.items);
    return itemsWithMissingLinks.map((item) => ({
      fulfillmentPlanItemId: item.fulfillmentPlanItemId,
      fulfillmentPlanId: fulfillmentPlan.fulfillmentPlanId,
    }));
  });
};

const isTableDataValid = (tableData) => {
  const entriesWithMissingLinks = filterItemsWithMissingLinks(tableData);
  return entriesWithMissingLinks.length === 0;
};

export const useInitializeSplitShipModal = (fulfillmentPlanIds) => {
  const fulfillmentPlans = useEntitiesFulfillmentPlansFromFulfillmentPlanIds(fulfillmentPlanIds);
  const [isInitializing, setIsInitializing] = useState(true);
  const [fetchResult, setFetchResult] = useState({});
  const { initialFulfillmentPlans, initialTableData } = getInitialData(fulfillmentPlans);

  useEffect(() => {
    // todo: I'm pretty sure none of the stuff around "items with missing links" is needed anymore now that items have a
    //  `mergeKey`. I'm leaving it in for now so we can track any calls to the `getMissingLinks` endpoint, but if traffic
    //  to that endpoint stops, we should remove this code.
    const missingLinks = getItemsWithMissingLinks(fulfillmentPlans);
    const shouldFetchMissingLinks = !!missingLinks.length;
    if (!shouldFetchMissingLinks) {
      setIsInitializing(false);
      return;
    }

    getMissingLinks({ links: missingLinks })
      .then((res) => {
        const patchedLinks = res.data.links;
        setFetchResult({ patchedLinks, hasError: false });
      })
      .catch(() => {
        handlePatchFailure(missingLinks);
        setFetchResult({ patchedLinks: null, hasError: true });
      })
      .finally(() => {
        setIsInitializing(false);
      });
  }, [fulfillmentPlans]);

  const key = fetchResult.patchedLinks ? 'patched-data' : 'initial-data';
  const patchData = fetchResult.hasError ? [] : fetchResult.patchedLinks;
  const patchedInitialTableData = getPatchedTableData(initialTableData, patchData);
  const hasError = !isInitializing && !isTableDataValid(patchedInitialTableData);

  return {
    key,
    initialFulfillmentPlans,
    initialTableData: patchedInitialTableData,
    isInitializing,
    hasError,
  };
};
